import { P2Accordion, P2TextDescription, P2TextLabel } from "react-bootstrap-common-script";
import { Separator } from "../../separator";
import AccordionTitleAmoount from "../../accordion/accordion-title-amount/accordion-title-amount";
import { InfoCard } from "../../info";
import { InfoCircle } from "../../../assets/svg";
import AdjustFPInputGroup from "../../HOC/adjust-fp-input-group/adjust-fp-input-group";
import { Helpers } from "../../../utils/helper";
import { useMemo } from "react";
import { useFinancialPlanContext } from "../../../context/financial-plan-contenxt";
import { updateArrayState, updateSingleState } from "../../../utils/financial-plan";

const AdjustProposedPaymentAllocationSection = () => {
  const {
    constant: { owner_details },
    indexOfCurrentCpfDetail,
    cpfDetails,
    loanDetail,
    setIndexOfCurrentCpfDetail,
    setCpfDetails,
    setLoanDetail,
    derivedValue: { payableByCpfExcludingFees, payableByCash },
  } = useFinancialPlanContext();

  const AdjustedDate = {
    hdb_value: 952122,
    commencement_date: "2024-08-01",
    owners: [
      {
        name: "Owner 1",
        cpf_amount: 60000,
      },
      {
        name: "Owner 1",
        cpf_amount: 38000,
      },
    ],
  };

  const monthlyInstallment = useMemo(() => {
    //CALCULATE
    return "0";
  }, []);
  const interestRate = useMemo(() => {
    //CALCULATE
    return "2.6";
  }, []);

  const areOthersZero = (payableByCpfExcludingFees + payableByCash + loanDetail.loan_val - cpfDetails[indexOfCurrentCpfDetail].cpf_val) <= 0;
  const createHandleCpf = (index: number, uinfin: string) => {
    return (value: number) => {
      const isIncreasing = value > cpfDetails[indexOfCurrentCpfDetail].cpf_val;
      if (areOthersZero && isIncreasing) {
        return
      }

      setIndexOfCurrentCpfDetail(index)
      updateArrayState(setCpfDetails, "cpf_val", value, "uinfin", uinfin);
    }
  }

  return (
    <div>
      <div style={{ flex: 1 }}>
        <P2TextDescription color="#333" title={"Proposed Payment Allocation"} />
        <div style={{ fontStyle: "italic" }}>
          <P2TextLabel color="#333" fontSize={"1.125rem"} bold={400} label={"Adjusted by XXXX on 11 May 2024, 11:43AM"} />
        </div>
      </div>

      <div className="mt-3">
        <P2TextLabel
          color="#333"
          fontSize={"1.125rem"}
          bold={400}
          label={
            "You may adjust your CPF payment (excluding fees), loan amount and repayment period below. The cash and fee payment will automatically update to meet the payment required. You can also pick your payment options for the fees in the fees payable section."
          }
        />
      </div>

      <div style={{ paddingTop: "32px", display: "flex", flexDirection: "column", gap: 32 }}>
        <P2Accordion
          defaultOpen={true}
          title={
            <AccordionTitleAmoount
              title="Payable by CPF (excluding fees)"
              description={Helpers.formatThousandSeparator(payableByCpfExcludingFees.toFixed(2), "S$")}
            />
          }
        >
          <Separator />
          <div style={{ paddingTop: "32px", display: "flex", flexDirection: "column", gap: 32 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
              {cpfDetails.map((cpf_detail, index) => {
                const ownerDetail = owner_details.find((owner_detail) => owner_detail.uinfin === cpf_detail.uinfin);
                const ownerName = ownerDetail?.name ?? "";
                return (
                  <AdjustFPInputGroup
                    title={ownerName}
                    minLabel="Minimum amount"
                    maxLabel="Maximum amount"
                    minValue={cpf_detail.cpf_min}
                    value={cpf_detail.cpf_val}
                    setValue={createHandleCpf(index, cpf_detail.uinfin)}
                    maxValue={cpf_detail.cpf_max}
                    hasDecimal={true}
                  />
                );
              })}
            </div>
            <InfoCard
              icon={<InfoCircle />}
              description={
                <p>
                  The default amounts are the maximum withdrawable amounts from your respective CPF Ordinary Accounts (OA). You may reduce these amounts to the
                  extent that each of your CPF OAs retains a maximum balance of $20,000.00.
                </p>
              }
            />
          </div>
        </P2Accordion>

        <P2Accordion
          defaultOpen={true}
          title={<AccordionTitleAmoount title="Payable by Loan" description={Helpers.formatThousandSeparator(loanDetail.loan_val.toFixed(2), "S$")} />}
        >
          <Separator />
          <div style={{ paddingTop: "32px", display: "flex", flexDirection: "column", gap: 32 }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <AdjustFPInputGroup
                  title="Loan amount"
                  maxLabel="Maximum amount"
                  maxValue={300000}
                  value={loanDetail.loan_val}
                  setValue={(value) => updateSingleState(setLoanDetail, "loan_val", value)}
                  hasDecimal
                  hasTooltip
                />
                <InfoCard
                  icon={<InfoCircle />}
                  description={
                    <p>The loan amount must be specified in $100 increments, and it affects conveyancing and stamp fees and Home Protection Scheme premiums.</p>
                  }
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <AdjustFPInputGroup
                  title="Repayment period"
                  minLabel="Minimum period"
                  maxLabel="Maximum period"
                  minValue={1}
                  maxValue={25}
                  value={loanDetail.repayment_period}
                  setValue={(value) => updateSingleState(setLoanDetail, "repayment_period", value)}
                  hasTooltip
                />
                <InfoCard icon={<InfoCircle />} description={<p>Repayment period affects the Home Protection Scheme premiums.</p>} />
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
              <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                <P2TextLabel fontSize={"1.125rem"} bold={700} label={"Interest rate"} />
                <P2TextLabel fontSize={"1.125rem"} bold={700} label={`${interestRate}%`} />
              </div>
              <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                <P2TextLabel fontSize={"1.125rem"} bold={700} label={"Monthly instalment"} />
                <P2TextLabel fontSize={"1.125rem"} bold={700} label={Helpers.formatThousandSeparator(monthlyInstallment, "$")} />
              </div>
              <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                <P2TextLabel fontSize={"1.125rem"} bold={700} label={"Instalment commencement date"} />
                <P2TextLabel fontSize={"1.125rem"} bold={700} label={Helpers.formatDate(AdjustedDate.commencement_date)} />
              </div>
            </div>
          </div>
        </P2Accordion>
      </div>
    </div>
  );
};

export default AdjustProposedPaymentAllocationSection;
